import * as React from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { DateRange } from "@mui/lab";
import DateRangeChip from "./DateRangeChip";
import ColumnFiltersInput from "./ColumnFiltersInput";
import SearchInput from "./SearchInput";
import CalendarModal from "./CalendarModal";
import SearchInputChip from "./SearchInputChip";
import RoleFilter from "./RoleFilter";
import { PmSearchField, TransSearchField } from "@inosio/payments-sdk";
import { ChangeEvent } from "react";
import CloseIcon from '@mui/icons-material/Close';
import CalendarIcon from "../../icons/CalendarIcon.svg";
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * !No further development using this file or within this file. This component is way too tightly coupled with
 * the consumers. A new version has been created: SearchableTableHeader.tsx.
 */
interface TableHeaderPropTypes {
  placeholder?: string;
  searchValue: string;
  isSearchValueEmpty: boolean;
  searchField: string;
  optionValues: { key: PmSearchField | string | TransSearchField; value: string }[];
  columnsSelectRef: React.RefObject<HTMLSelectElement>;
  columnFilterSearchField: string;
  searchChipOpen?: boolean;
  searchBluePillValue: string;
  searchValueInputRef: React.RefObject<HTMLInputElement>;
  rangeSize?: number;
  recordStartIndex?: number;
  depositId?: string | null;
  roleOptions?: { key: string; value: string }[];
  hideCalendarIcon?: boolean;
  hideCalendarModal?: boolean;
  hideSearchInput?: boolean;
  filteredRole?: string;
  calendarWidgetDateRange?: DateRange<Date>;
  defaultEndDate?: Date;
  defaultStartDate?: Date;
  dateRangeChipOpen?: boolean;
  roleSelectRef?: React.RefObject<HTMLSelectElement>;
  hideDateRangeSearchPillChip?: boolean;
  depositChipOpen?: boolean;
  openCalendarModal?: boolean;
  searchValueChangeHandler: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  searchFieldChangeHandler: (e: any) => void;
  handleSearch: (e: any) => void;
  handleChooseColumnSearchField: (e: any) => void;
  resetTableRows?: () => void;
  resetSearchFilter: React.Dispatch<React.SetStateAction<string>>;
  resetSearchValue: React.Dispatch<React.SetStateAction<string>>;
  roleFilteredChangeHandler?: (e: any) => void;
  resetFilterRole?: React.Dispatch<React.SetStateAction<string>>;
  setcalendarWidgetDateRange?: React.Dispatch<React.SetStateAction<DateRange<Date>>>;
  routeChange?: () => void;
  setSearchChipOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setDateRangeChipOpen?: (value: React.SetStateAction<boolean>) => void;
  handleClickOpen?: () => void;
  setDepositChipOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose?: () => void;
  handleCalendarSubmit?: (val: DateRange<Date>) => void;
  defaultSearchParam?: string;
}

/**
 * @deprecated Do not use this. Use the new SearchTableHeader instead
 * @param props 
 * @returns 
 */
const TableHeader = (props: TableHeaderPropTypes) => {
  const matchesMobile = useMediaQuery('(max-width:420px)');
  const matchesIpad = useMediaQuery('(max-width:768px)');

  const [errorMsg, setErrorMsg] = React.useState('');

  const handleSearch = (e: any) => {
    if (props.searchValue?.length !== 0 && props.searchField.length !== 0) {
      setErrorMsg('');
      props.handleSearch(e);
    } else if (props.searchValue?.length !== 0 && props.searchField.length === 0) {
      setErrorMsg('Please select a filter');
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          p: 0.5,
          pb: matchesIpad ? 0 : 2,
          flexDirection: matchesIpad ? 'column' : 'row',
          justifyContent: "end",
          alignItems: matchesIpad ? "baseline" : "center",
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box style={{ marginRight: '13px' }}>
            <ColumnFiltersInput searchFieldChangeHandler={props.searchFieldChangeHandler} selectedValue={props.searchField} optionValues={props.optionValues} columnsSelectRef={props.columnsSelectRef} />
          </Box>

          <Box style={{ marginRight: matchesMobile ? '10px' : '20px' }}>
            {props?.hideSearchInput && props?.searchField === "Role" ? (
              <RoleFilter roleOptions={props.roleOptions} roleFilteredChangeHandler={props.roleFilteredChangeHandler} roleSelectRef={props?.roleSelectRef} filteredRole={props.filteredRole} />
            ) : (
              <SearchInput
                handleSearch={handleSearch}
                searchValueInputRef={props.searchValueInputRef}
                searchValueChangeHandler={props.searchValueChangeHandler}
                searchValue={props.searchValue}
                disableSearchIcon={!props.searchField}
                isSearchValueEmpty={props.isSearchValueEmpty}
                handleChooseColumnSearchField={props.handleChooseColumnSearchField}
                placeholder={props.placeholder}
                width={matchesMobile ? '130px' : matchesIpad ? '200px' : undefined}
              />
            )}
          </Box>

          {props.depositChipOpen &&
            <Box style={{ marginRight: matchesMobile ? '10px' : '20px' }}>
              <Chip
                label={`Deposit ID: ${props.depositId}`}
                sx={{
                  border: "2px #39A2DB solid",
                  bgcolor: "#ffffff",
                  color: "#39A2DB",
                  height: "42px",
                  fontSize: "18px",
                  lineHeight: "22px",
                  borderRadius: '24px'
                }}
                onDelete={() => {
                  props.resetSearchValue("");
                  props?.routeChange && props.routeChange();
                  props?.setDepositChipOpen && props.setDepositChipOpen(false);
                  props?.resetTableRows && props?.resetTableRows();
                }}
                deleteIcon={<CloseIcon sx={{ color: "#39A2DB !important" }} />}
              />
            </Box>
          }

          {!props?.hideCalendarIcon && <Box style={{ marginRight: '24px' }}>
            <IconButton aria-label="calendar" onClick={props.handleClickOpen} >
              <img src={CalendarIcon} alt="calendar icon" width="24px" height="24px" />
            </IconButton>
          </Box>}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: matchesIpad ? 1.5 : 0 }}>
          {props.searchField.length !== 0 &&
            <SearchInputChip
              searchChipOpen={props?.searchChipOpen}
              routeChange={props.routeChange}
              setSearchChipOpen={() => props?.setSearchChipOpen && props.setSearchChipOpen(false)}
              searchBluePillValue={props.searchBluePillValue}
              columnFilterSearchField={props.optionValues.find((option: any) => option.key === props.columnFilterSearchField)?.value}
              resetFilter={props.resetSearchFilter}
              resetSearchValue={props.resetSearchValue}
              resetTableRows={props.resetTableRows}
              filteredRole={props.filteredRole}
              resetFilterRole={props.resetFilterRole}
              searchField={props.searchField}
              defaultSearchParam={props.defaultSearchParam}
            />
          }

          {!props?.hideDateRangeSearchPillChip && props.dateRangeChipOpen &&
            <DateRangeChip
              calendarWidgetDateRange={props.calendarWidgetDateRange}
              onDelete={() => {
                if (props?.setDateRangeChipOpen) {
                  props.setDateRangeChipOpen(false);
                }
                if (props?.setcalendarWidgetDateRange) {
                  props.setcalendarWidgetDateRange([props.defaultStartDate ?? null, props.defaultEndDate ?? null]);
                }
              }}
            />
          }
        </Box>

        {!props?.hideCalendarModal &&
          <CalendarModal
            value={props.calendarWidgetDateRange}
            handleClose={props.handleClose}
            open={props.openCalendarModal}
            handleCalendarSubmit={(val: DateRange<Date>) => {
              if (props?.setcalendarWidgetDateRange) {
                props.setcalendarWidgetDateRange(val);
              }
              if (props?.handleCalendarSubmit) {
                props?.handleCalendarSubmit(val);
              }
            }} />}
      </Box>

      {errorMsg.length !== 0 && (
        <Typography sx={{
          fontWeight: 400,
          fontFamily: "Work Sans",
          fontSize: "14px",
          lineHeight: "18px",
          color: "#d32f2f",
          marginLeft: "18px",
          marginTop: "-5px",
        }}>
          {errorMsg}
        </Typography>
      )}
    </Box>
  );
};

export default TableHeader;
